"use client";

import React, { useState } from "react";
import {
  Loader2,
  Newspaper,
  ChevronRight,
  User,
  Calendar,
} from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";

// Notícias com conteúdo completo para leitura
const NEWS_ITEMS = [
  {
    id: 1,
    category: "Mercado",
    title: "Fed mantiene postura cautelosa ante inflación",
    excerpt:
      "La Reserva Federal señaló que seguirá monitoreando los datos antes de tomar decisiones sobre tasas.",
    date: "Hoy",
    author: "Equipo AI Club",
    content: `La Reserva Federal de Estados Unidos mantuvo su postura cautelosa en la última reunión de política monetaria. Los funcionarios señalaron que seguirán monitoreando de cerca los datos de inflación antes de tomar decisiones sobre las tasas de interés.

El presidente de la Fed destacó que la evolución de los precios sigue siendo el factor determinante para futuros ajustes. Los mercados esperan que la institución mantenga las tasas en niveles restrictivos hasta tener mayor claridad sobre la trayectoria inflacionaria.

Los analistas coinciden en que la prudencia es clave en el actual escenario económico, donde las señales mixtas requieren un enfoque gradual y basado en datos.`,
  },
  {
    id: 2,
    category: "Tecnología",
    title: "Sector tech lidera ganancias en la bolsa",
    excerpt:
      "Las empresas de tecnología continúan mostrando fortaleza en un contexto de expectativas positivas.",
    date: "Hoy",
    author: "Equipo AI Club",
    content: `El sector tecnológico ha sido uno de los principales impulsores del mercado de valores en las últimas sesiones. Las empresas de tecnología continúan mostrando fortaleza en un contexto de expectativas positivas sobre la demanda y la innovación.

La inteligencia artificial y la computación en la nube siguen siendo temas centrales para los inversores. Las grandes compañías del sector han reportado resultados que superan las expectativas del mercado.

Los expertos recomiendan a los inversores educarse sobre los fundamentos antes de tomar decisiones. La diversificación y el conocimiento del perfil de riesgo son pilares de una estrategia responsable.`,
  },
  {
    id: 3,
    category: "Economía",
    title: "Mercados reaccionan a datos de empleo",
    excerpt:
      "Los indicadores laborales influyen en las expectativas de los inversores sobre el rumbo de la economía.",
    date: "Ayer",
    author: "Equipo AI Club",
    content: `Los mercados financieros han mostrado alta sensibilidad a la publicación de los últimos datos de empleo. Los indicadores laborales son clave para entender la salud de la economía y las expectativas sobre el rumbo de las políticas monetarias.

Cuando el empleo se mantiene fuerte, los inversores suelen ajustar sus expectativas sobre las tasas de interés. Esto puede generar volatilidad en distintos sectores del mercado.

Es importante recordar que los datos económicos forman parte del contexto general. Los inversores educados consideran múltiples factores antes de tomar decisiones sobre su portafolio.`,
  },
  {
    id: 4,
    category: "Energía",
    title: "Petróleo con volatilidad por tensiones geopolíticas",
    excerpt:
      "El crudo enfrenta presión por incertidumbre en la oferta global.",
    date: "Ayer",
    author: "Equipo AI Club",
    content: `El mercado del petróleo ha experimentado días de alta volatilidad debido a las tensiones geopolíticas en regiones productoras. El crudo enfrenta presión por la incertidumbre en la oferta global y las expectativas sobre la demanda.

Los precios de las materias primas energéticas suelen reaccionar con rapidez a eventos geopolíticos. Esta volatilidad puede afectar a distintos sectores de la economía y del mercado de valores.

Para los inversores, entender la relación entre commodities y mercados financieros es parte de la educación necesaria para tomar decisiones informadas.`,
  },
  {
    id: 5,
    category: "Educación",
    title: "¿Qué es la diversificación?",
    excerpt: "Conoce el concepto clave para reducir el riesgo en tu portafolio.",
    date: "Hace 2 días",
    author: "Equipo AI Club",
    content: `La diversificación es uno de los principios fundamentales de la inversión. Consiste en distribuir el capital entre diferentes activos, sectores o regiones para reducir el riesgo del portafolio.

El concepto se basa en la idea de que no todos los activos se comportan igual al mismo tiempo. Cuando algunos bajan, otros pueden subir, suavizando el impacto en el resultado total.

Para diversificar correctamente, es importante conocer tu perfil de riesgo y tus objetivos.`,
  },
  {
    id: 6,
    category: "Mercado",
    title: "Índices globales en zona de consolidación",
    excerpt: "Los principales mercados buscan dirección ante expectativas mixtas.",
    date: "Hace 2 días",
    author: "Equipo AI Club",
    content: `Los principales índices bursátiles globales se encuentran en una fase de consolidación. Los mercados buscan dirección ante un escenario de expectativas mixtas sobre el crecimiento económico y la inflación.

En estos períodos, es común observar menor volumen de operaciones y movimientos laterales. Los inversores suelen esperar señales más claras antes de tomar posiciones decisivas.`,
  },
  {
    id: 7,
    category: "Tecnología",
    title: "IA revoluciona el análisis de mercados",
    excerpt: "Herramientas de inteligencia artificial ganan espacio entre inversores.",
    date: "Hace 3 días",
    author: "Equipo AI Club",
    content: `Las herramientas de inteligencia artificial están transformando la forma en que los inversores analizan el mercado. Plataformas educativas incorporan estas tecnologías para ofrecer contenido personalizado.

Es importante recordar que la IA es una herramienta de apoyo. La educación financiera y el juicio crítico siguen siendo fundamentales para tomar decisiones informadas.`,
  },
  {
    id: 8,
    category: "Economía",
    title: "Inflación en zona objetivo en varias economías",
    excerpt: "Los bancos centrales monitorean la evolución de los precios.",
    date: "Hace 3 días",
    author: "Equipo AI Club",
    content: `Varias economías han logrado llevar la inflación hacia sus zonas objetivo. Los bancos centrales mantienen una postura vigilante ante posibles repuntes.

Para los inversores, entender el ciclo de tasas de interés ayuda a anticipar movimientos en bonos y acciones. La educación continua es clave en mercados dinámicos.`,
  },
  {
    id: 9,
    category: "Energía",
    title: "Transición energética impulsa inversiones",
    excerpt: "Las energías renovables atraen capital de largo plazo.",
    date: "Hace 4 días",
    author: "Equipo AI Club",
    content: `La transición hacia fuentes de energía más limpias sigue atrayendo inversiones significativas. Solar y eólica lideran el crecimiento en capacidad instalada.

Los inversores consideran tanto el potencial de retorno como el impacto ambiental. La diversificación entre sectores tradicionales y renovables puede equilibrar el portafolio.`,
  },
  {
    id: 10,
    category: "Mercado",
    title: "Bonos corporativos con demanda sólida",
    excerpt: "Los inversores buscan renta fija con mejor rendimiento.",
    date: "Hace 4 días",
    author: "Equipo AI Club",
    content: `La demanda por bonos corporativos se mantiene sólida en un contexto de tasas atractivas. Los inversores buscan alternativas a la renta fija gubernamental.

Conocer la diferencia entre bonos de alta calidad y de mayor riesgo es parte de la educación necesaria para construir un portafolio equilibrado.`,
  },
  {
    id: 11,
    category: "Educación",
    title: "¿Cómo leer un balance financiero?",
    excerpt: "Aprende los conceptos básicos para evaluar empresas.",
    date: "Hace 5 días",
    author: "Equipo AI Club",
    content: `El balance financiero es uno de los documentos clave para evaluar la salud de una empresa. Muestra activos, pasivos y patrimonio en un momento determinado.

Entender conceptos como liquidez, endeudamiento y rentabilidad ayuda a tomar decisiones más informadas. La educación financiera es el primer paso.`,
  },
  {
    id: 12,
    category: "Tecnología",
    title: "Ciberseguridad: tema clave para inversores",
    excerpt: "Las empresas priorizan la protección de datos.",
    date: "Hace 5 días",
    author: "Equipo AI Club",
    content: `La ciberseguridad se ha convertido en un factor relevante para evaluar empresas. Inversores consideran la resiliencia digital como parte del análisis.

Sectores como fintech y salud manejan datos sensibles. La gestión de riesgos incluye evaluar cómo las empresas protegen la información.`,
  },
  {
    id: 13,
    category: "Economía",
    title: "Comercio global muestra señales de recuperación",
    excerpt: "Los flujos comerciales se normalizan tras tensiones.",
    date: "Hace 6 días",
    author: "Equipo AI Club",
    content: `Los indicadores de comercio global muestran señales de recuperación. Las cadenas de suministro se adaptan a un nuevo contexto geopolítico.

Para inversores en empresas exportadoras, el comercio internacional es un factor a monitorear. La diversificación geográfica del portafolio puede mitigar riesgos.`,
  },
  {
    id: 14,
    category: "Mercado",
    title: "Dividendos: estrategia de ingresos pasivos",
    excerpt: "Conoce cómo las empresas reparten utilidades.",
    date: "Hace 6 días",
    author: "Equipo AI Club",
    content: `Las acciones que pagan dividendos atraen a inversores que buscan ingresos recurrentes. La sostenibilidad del pago depende de la salud financiera de la empresa.

Evaluar el ratio de pago y la historia de dividendos ayuda a identificar oportunidades. La paciencia y el enfoque a largo plazo son aliados.`,
  },
  {
    id: 15,
    category: "Energía",
    title: "Gas natural con alta volatilidad",
    excerpt: "El mercado energético enfrenta factores estacionales.",
    date: "Hace 7 días",
    author: "Equipo AI Club",
    content: `El gas natural ha mostrado alta volatilidad por factores estacionales y de oferta. Los precios reaccionan a inventarios y condiciones climáticas.

Los commodities energéticos requieren conocimiento específico. La educación sobre estos mercados ayuda a entender los movimientos sin tomar decisiones impulsivas.`,
  },
  {
    id: 16,
    category: "Educación",
    title: "Riesgo y retorno: la relación fundamental",
    excerpt: "Mayor retorno potencial suele implicar mayor riesgo.",
    date: "Hace 7 días",
    author: "Equipo AI Club",
    content: `La relación entre riesgo y retorno es uno de los pilares de las finanzas. Generalmente, mayores retornos potenciales implican asumir más riesgo.

Conocer tu tolerancia al riesgo es esencial antes de invertir. Un portafolio alineado con tu perfil evita decisiones emocionales en momentos de volatilidad.`,
  },
  {
    id: 17,
    category: "Tecnología",
    title: "Semiconductores: motor de la economía digital",
    excerpt: "El sector mantiene relevancia estratégica global.",
    date: "Hace 8 días",
    author: "Equipo AI Club",
    content: `Los semiconductores son la base de la economía digital. El sector mantiene relevancia estratégica y atrae inversiones en capacidad productiva.

La complejidad de la cadena de suministro hace que el sector sea volátil. Los inversores educados entienden los ciclos antes de tomar posiciones.`,
  },
  {
    id: 18,
    category: "Economía",
    title: "PIB y su impacto en los mercados",
    excerpt: "El crecimiento económico influye en las expectativas.",
    date: "Hace 8 días",
    author: "Equipo AI Club",
    content: `El Producto Interno Bruto (PIB) es un indicador clave de la salud económica. Su evolución influye en las expectativas de inversores y en las políticas monetarias.

Entender los ciclos económicos ayuda a contextualizar los movimientos del mercado. La educación financiera permite interpretar estos datos con perspectiva.`,
  },
  {
    id: 19,
    category: "Mercado",
    title: "ETF: diversificación en un solo instrumento",
    excerpt: "Los fondos cotizados democratizan el acceso a mercados.",
    date: "Hace 9 días",
    author: "Equipo AI Club",
    content: `Los ETF (fondos cotizados) permiten diversificar con un solo instrumento. Replican índices o sectores, democratizando el acceso a distintos mercados.

Conocer los costos, la liquidez y la estrategia subyacente es importante antes de invertir. Son una herramienta útil para portafolios diversificados.`,
  },
  {
    id: 20,
    category: "Educación",
    title: "Planificación financiera: el primer paso",
    excerpt: "Definir metas y plazos antes de invertir.",
    date: "Hace 9 días",
    author: "Equipo AI Club",
    content: `La planificación financiera es el primer paso antes de invertir. Definir metas, plazos y nivel de riesgo aceptable guía las decisiones.

Un plan escrito ayuda a mantener la disciplina en momentos de volatilidad. La educación continua refina la estrategia con el tiempo.`,
  },
];

function NewsArticleSheet({ article, open, onOpenChange }) {
  if (!article) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="h-[90vh] max-h-[90vh] rounded-t-2xl p-0 flex flex-col"
      >
        <SheetHeader className="p-4 pb-2 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
            <span className="font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-lg">
              {article.category}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              {article.date}
            </span>
          </div>
          <SheetTitle className="text-left text-xl font-bold text-gray-900 leading-tight">
            {article.title}
          </SheetTitle>
          <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
            <User className="w-4 h-4" />
            <span>{article.author}</span>
          </div>
        </SheetHeader>
        <ScrollArea className="flex-1 px-4 pb-6">
          <div className="pr-4 space-y-4 text-gray-700 leading-relaxed whitespace-pre-line">
            {article.content}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

function HomeContent() {
  const { loading } = useAuth();
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const openArticle = (article) => {
    setSelectedArticle(article);
    setSheetOpen(true);
  };

  const closeArticle = () => {
    setSheetOpen(false);
    setTimeout(() => setSelectedArticle(null), 300);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-gray-50 pt-[70px] pb-[100px]"
    >
      <div className="max-w-md mx-auto px-4 py-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30">
              <Newspaper className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Portal de Noticias
              </h1>
              <p className="text-gray-500 text-sm">
                Mercado y bolsa de valores • Toca para leer
              </p>
            </div>
          </div>
        </motion.div>

        {/* Stats bar */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="flex gap-4 mb-6 p-3 bg-white rounded-xl border border-gray-100"
        >
          <div className="flex-1 text-center">
            <p className="text-2xl font-bold text-blue-600">
              {NEWS_ITEMS.length}
            </p>
            <p className="text-xs text-gray-500">Artículos</p>
          </div>
          <div className="w-px bg-gray-200" />
          <div className="flex-1 text-center">
            <p className="text-2xl font-bold text-gray-900">5</p>
            <p className="text-xs text-gray-500">Categorías</p>
          </div>
        </motion.div>

        {/* News List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-3"
        >
          {NEWS_ITEMS.map((item, index) => (
            <motion.article
              key={item.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * index }}
              onClick={() => openArticle(item)}
              className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:shadow-md hover:border-blue-100 transition-all cursor-pointer active:scale-[0.98]"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-lg">
                      {item.category}
                    </span>
                  </div>
                  <h2 className="font-semibold text-gray-900 mt-2 line-clamp-2">
                    {item.title}
                  </h2>
                  <p className="text-gray-500 text-sm mt-1 line-clamp-2">
                    {item.excerpt}
                  </p>
                  <p className="text-gray-400 text-xs mt-2">{item.date}</p>
                </div>
                <div className="flex-shrink-0 mt-1 p-2 rounded-lg bg-gray-50 group-hover:bg-blue-50 transition-colors">
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </div>
            </motion.article>
          ))}
        </motion.div>

        <p className="text-center text-gray-400 text-xs mt-6">
          Contenido informativo. Complementa tu curso de inversiones.
        </p>
      </div>

      <NewsArticleSheet
        article={selectedArticle}
        open={sheetOpen}
        onOpenChange={(open) => {
          setSheetOpen(open);
          if (!open) setTimeout(() => setSelectedArticle(null), 300);
        }}
      />
    </motion.div>
  );
}

export default function Home() {
  return <HomeContent />;
}
